import { LocalizationService, PermissionService } from '@abp/ng.core';
import { Component, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { PrimengTableHelper } from './helpers/PrimengTableHelper';
import { Confirmation, ConfirmationService, Toaster, ToasterService } from '@abp/ng.theme.shared';
import { firstValueFrom } from 'rxjs';
import { EnumDescriptor } from './enum-descriptor';
import { AppConsts } from './AppConsts';

@Component({
    template: '',
})
export abstract class AppBaseComponent {
    
    readonly primengTableHelper: PrimengTableHelper;
    readonly Hyphen = AppConsts.Hyphen;
    readonly DatePickerFormat = AppConsts.DatePickerFormat;
    readonly DateFormat = AppConsts.DateFormat;
    readonly DateTimeFormat = AppConsts.DateTimeFormat;
    readonly SmartInvoicing = AppConsts.SmartInvoicing;
    readonly MinAutocompleteCharsToSearch = AppConsts.MinAutocompleteCharsToSearch;

    protected readonly titleService: Title;
    protected readonly permission: PermissionService;
    
    private readonly localization: LocalizationService;
    private readonly spinner: NgxSpinnerService;

    private readonly toaster: ToasterService;
    private readonly confirmation: ConfirmationService;

    constructor(injector: Injector) {
        this.titleService = injector.get(Title);
        this.localization = injector.get(LocalizationService);
        this.spinner = injector.get(NgxSpinnerService);
        this.permission = injector.get(PermissionService);
        this.toaster = injector.get(ToasterService);
        this.confirmation = injector.get(ConfirmationService);

        this.primengTableHelper = new PrimengTableHelper();
    }

    protected l(key: string, ...args: any[]): string {
        let localizedText = this.localization.instant(key, ...args);

        if (!localizedText) {
          localizedText = key;
        }
    
        return localizedText;
    }

    protected setTitle(title: string): void {
        this.titleService.setTitle(title);
    }

    protected getDefaultTitle(): string {
        return `${AppConsts.DefaultTitle}`;
    }

    protected showLoading(): void {
        this.spinner.show();
      }
    
    protected hideLoading(): void {
        this.spinner.hide();
    }

    protected hasPermission(permissionName: string): boolean {
        return this.permission.getGrantedPolicy(permissionName);
    }
    
    protected notifySuccess(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.success(message, title, options);
    }
    
    protected notifyInfo(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.info(message, title, options);
    }
    
    protected notifyError(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.error(message, title, options);
    }
    
    protected notifyWarn(message: string, title?: string, messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []): void {
        const options = this.generateOptionsParamsForNotify(messageLocalizationParams, titleLocalizationParams);
        this.toaster.warn(message, title, options);
    }    

    protected isNullOrUndefined(value: any): boolean {
        return value === null || value === undefined;
    }

    isEmptyString(value: string): boolean {
        return this.isNullOrUndefined(value) || value === '' || value.toString().trim().length === 0;
    }

    protected async confirmAndExecute<T>(message: string, confirmedAction: () => T,
        messageLocalizationParams: string[] = [], titleLocalizationParams: string[] = []
    ): Promise<void> {
        const options = this.generateOptionsParamsForConfirm(messageLocalizationParams, titleLocalizationParams);
        const statusResponse = await firstValueFrom(
            this.confirmation.warn(message, '::AreYouSureToPerformThisAction', options)
        );
    
        if (statusResponse === Confirmation.Status.confirm) {
            confirmedAction();
        }
    }

    private generateOptionsParamsForNotify(messageLocalizationParams: string[], titleLocalizationParams: string[]): Partial<Toaster.ToastOptions> {
        const options: Partial<Toaster.ToastOptions> = {
            life: 8000,
            sticky: false,
            closable: true,
            tapToDismiss: true,
            messageLocalizationParams: messageLocalizationParams,
            titleLocalizationParams: titleLocalizationParams,
        };

        return options;
    }

    private generateOptionsParamsForConfirm(messageLocalizationParams: string[], titleLocalizationParams: string[]): Partial<Toaster.ToastOptions> {
        const options: Partial<Confirmation.Options> = {
            hideCancelBtn: false,
            hideYesBtn: false,
            dismissible: false,
            cancelText: '::Close',
            yesText: '::Confirm',
            messageLocalizationParams: messageLocalizationParams,
            titleLocalizationParams: titleLocalizationParams,
          };
        
        return options;
    }

    protected getEnumOptions(enumObj: any, addAllOption = false): EnumDescriptor[] {
        let items = [];
        if (addAllOption) {
            items.push({
                label: this.l('::All'),
                value: -1,
            });
        }

        items = items.concat(
            Object.keys(enumObj)
                .filter(key => isNaN(Number(key)))
                .map(key => ({ label: this.l(`::${key}`), value: enumObj[key] }))
        );

        return items;
    }

    protected parseDateFromString(timeString: string): Date {
        const today = new Date();
        const [hours, minutes, seconds] = timeString.split(':').map(Number);
        today.setHours(hours, minutes, seconds || 0, 0);
        return new Date(today);
    }
    
    protected isDefinedAndStrictlyPositiveNumber(value: number): boolean {
        return this.isDefinedAndIsANumber(value) && value > 0;
    }

    protected isDefinedAndPositiveNumber(value: number): boolean {
        return this.isDefinedAndIsANumber(value) && value >= 0;
    }
    
    protected isDefined(value: any): boolean {
        return !this.isNullOrUndefined(value);
    }

    protected isValidEmail(email: string): boolean {
        if (this.isEmptyString(email)) {
            return false;
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email.trim());
    }

    protected isValidNumber(value: string): boolean {
        if (this.isEmptyString(value)) {
            return false;
        }

        const numberPattern = /^-?\d+(\.\d+)?$/;
        return numberPattern.test(value.trim());
    }
    
    private isDefinedAndIsANumber(value: number): boolean {
        return this.isDefined(value) && !isNaN(value);
    }
}